import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { VehicleHistorySearch } from "map/model";
import apiClient from "services/apiClient";
import { ActivityData } from "map/model";
import { TripTimelineEvent } from "trips/models";

export const getVehicleTripData = createAsyncThunk<any, VehicleHistorySearch>(
  "vehicle.getVehicleActivity",
  async (request: VehicleHistorySearch) => {
    if (!request.unitNumber || !request.startDate || !request.endDate) return;

    let uri = `/vehicles/activity?unitNumber=${request.unitNumber}
        &startDate=${new Date(request.startDate).toISOString()}
        &endDate=${new Date(request.endDate).toISOString()}`;

    if (request.clientName) {
      uri = `${uri}&clientName=${request.clientName}`;
    }

    var response = await apiClient.get(uri);
    return response;
  },
  {
    condition: (request, { getState }) => {
      const { vehicleTrip } = getState() as any;
      const lastRequest = vehicleTrip.lastVehicleRequest;
      // Skip if we already have data for the same request
      if (lastRequest && vehicleTrip.vehicleTripData) {
        const same = 
          lastRequest.unitNumber === request.unitNumber &&
          lastRequest.clientName === request.clientName &&
          new Date(lastRequest.startDate).getTime() === new Date(request.startDate).getTime() &&
          new Date(lastRequest.endDate).getTime() === new Date(request.endDate).getTime();
        
        if (same) {
          return false; // Skip this request
        }
      }
      
      return true; // Allow request to proceed
    }
  }
);

export const getTimeline = createAsyncThunk<any, { tripNumber: string; isActive?: string, isReload?: boolean, includeSuggested?: boolean }>(
  "vehicle.getTimeline",
  async ({ tripNumber, isActive, includeSuggested = true }) => {
    let url = `/events/timeline/${tripNumber}`;
    const params: string[] = [];
    if (isActive && isActive !== "all") {
      params.push(`isActive=${isActive === "active" ? "true" : "false"}`);
    }
    if (!includeSuggested) {
      params.push(`includeSuggested=false`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    var response = await apiClient.get(url);
    return response;
  },
  {
    condition: ({ tripNumber, isActive, isReload }, { getState }) => {
      const { vehicleTrip } = getState() as any;

      // Dedupe an in-flight fetch for the same trip/filter so simultaneously
      // mounting components don't fire identical requests. Past completion we
      // always refetch on the next dispatch — Redux state can grow stale
      // (another user editing in Flow, approval/clear procs run from the
      // legacy Dashboard) and serving the cached snapshot lets a subsequent
      // save round-trip stale IsActive/typeId values back into the DB.
      if (vehicleTrip.timelineLoading &&
          vehicleTrip.currentTripNumber === tripNumber &&
          vehicleTrip.currentIsActive === isActive &&
          !isReload) {
        return false; // Skip - already loading
      }

      return true; // Allow request to proceed
    }
  }
);

interface VehicleTripInfo {
  tripTimeline: TripTimelineEvent[];
  vehicleTripData: ActivityData;
  timeZone: string;
  totalDriveMiles?: number;
  totalDriveMinutes?: number;
  mapStyleReloadedCount?: number;
  currentTripNumber?: string;
  currentIsActive?: string;
  timelineLoading?: boolean;
  lastVehicleRequest?: VehicleHistorySearch;
  lastRequestId?: string;
  vehicleTripLoading?: boolean;
}

const initialState: VehicleTripInfo = {
  tripTimeline: undefined,
  vehicleTripData: undefined,
  timeZone: new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2],
  totalDriveMiles: 0,
  totalDriveMinutes: 0,
  mapStyleReloadedCount: 0,
  currentTripNumber: undefined,
  currentIsActive: undefined,
  timelineLoading: true,
  lastVehicleRequest: undefined,
  lastRequestId: undefined,
  vehicleTripLoading: false
};
export const vehicleTripSlice = createSlice({
  name: "vehicleTrip",
  initialState: initialState,
  reducers: {
    clearVehicleTripData: (state) => {
      state.vehicleTripData = undefined;
      state.lastVehicleRequest = undefined;
      state.lastRequestId = undefined;
    },
    clearTimeline: (state) => {
      state.tripTimeline = undefined;
      state.currentTripNumber = undefined;
      state.currentIsActive = undefined;
    },
    setTimeZone: (state, action) => {
      state.timeZone = action.payload;
    },
    setMapStyleReloadedCount: (state, action) => {
     // this controls if the layers are reloaded on style change.
     // if 0 don't reload; otherwise reload.
      state.mapStyleReloadedCount = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getVehicleTripData.pending, (state, action) => {
      state.lastRequestId = action.meta.requestId;
      state.vehicleTripLoading = true;
    });
    builder.addCase(getVehicleTripData.rejected, (state, action) => {
      if (action.meta.requestId !== state.lastRequestId) return;
      state.vehicleTripLoading = false;
    });
    builder.addCase(getVehicleTripData.fulfilled, (state, action) => {
      // Only update state if this is the most recent request
      if (action.meta.requestId !== state.lastRequestId) return;
      state.vehicleTripLoading = false;

      state.vehicleTripData = action.payload;
      state.lastVehicleRequest = action.meta.arg; // Store the request parameters
      if (state.vehicleTripData?.activity) {
        state.vehicleTripData.activity = state.vehicleTripData.activity.map((s: any, i) => ({ ...s, index: i + 1, id: i }));
      }
    });
    builder.addCase(getTimeline.pending, (state, action) => {
      // When the trip changes, drop the previous trip's timeline so it can't bleed
      // into the new trip's view during the network round-trip.
      if (state.currentTripNumber !== action.meta.arg.tripNumber) {
        state.tripTimeline = undefined;
        state.totalDriveMiles = 0;
        state.totalDriveMinutes = 0;
      }
      // Set these immediately when request starts to prevent duplicate requests
      state.currentTripNumber = action.meta.arg.tripNumber;
      state.currentIsActive = action.meta.arg.isActive;
      state.timelineLoading = true;
    });
    builder.addCase(getTimeline.fulfilled, (state, action) => {
      state.timelineLoading = false;
      if (action.payload?.events?.length > 0) {
        state.tripTimeline = action.payload.events.map((s: any, i) => ({ ...s, index: i + 1, id: i }));
        state.totalDriveMiles = action.payload.totalDriveTimeMiles;
        state.totalDriveMinutes = action.payload.totalDriveTimeMinutes;
      } else {
        state.tripTimeline = [];
      }
    });
    builder.addCase(getTimeline.rejected, (state) => {
      state.timelineLoading = false;
    });
  },
});

export const vehicleTripData = (state) => state.vehicleTrip.vehicleTripData;
export const tripTimeline = (state) => state.vehicleTrip.tripTimeline;
export const timeZone = (state) => state.vehicleTrip.timeZone;
export const totalDriveMiles = (state) => state.vehicleTrip.totalDriveMiles;
export const totalDriveMinutes = (state) => state.vehicleTrip.totalDriveMinutes;
export const mapStyleReloadedCount = (state) => state.vehicleTrip.mapStyleReloadedCount;
export const isTimelineLoading = (state) => state.vehicleTrip.timelineLoading;
export const isVehicleTripLoading = (state) => state.vehicleTrip.vehicleTripLoading;
// Action creators are generated for each case reducer function
export const { clearVehicleTripData, clearTimeline, setTimeZone, setMapStyleReloadedCount } = vehicleTripSlice.actions;

export default vehicleTripSlice.reducer;
